import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Zap, ChevronRight, FileText, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuickReferralSelector({ onSelect, onSkip }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            const response = await axios.get(`${API}/api/quick-referral-templates`);
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const getBodyPartIcon = (bodyPart) => {
        const icons = {
            knee: '🦵',
            hip: '🦴',
            shoulder: '💪',
            hand: '🤚',
            spine: '🔙',
            foot: '🦶',
            ankle: '🦶',
            elbow: '💪'
        };
        return icons[bodyPart] || '🏥';
    };

    const handleSelect = (template) => {
        setSelectedTemplate(template);
    };

    const handleConfirm = () => {
        if (selectedTemplate) {
            onSelect(selectedTemplate);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="quick-referral-selector">
            <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                    <Zap className="h-6 w-6 text-amber-500" />
                    <h2 className="text-xl font-semibold">Quick Referral</h2>
                </div>
                <p className="text-slate-600">
                    Select a template for common conditions or start from scratch
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => (
                    <Card 
                        key={template.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                            selectedTemplate?.id === template.id 
                                ? 'ring-2 ring-teal-500 bg-teal-50' 
                                : 'hover:bg-slate-50'
                        }`}
                        onClick={() => handleSelect(template)}
                        data-testid={`template-${template.id}`}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                                <span className="text-2xl">{getBodyPartIcon(template.body_part)}</span>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-slate-900">{template.name}</h3>
                                    <p className="text-sm text-slate-600 mt-1">{template.description}</p>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {template.common_symptoms.slice(0, 3).map((symptom, i) => (
                                            <Badge key={i} variant="secondary" className="text-xs">
                                                {symptom}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {selectedTemplate && (
                <Card className="bg-teal-50 border-teal-200">
                    <CardContent className="p-4">
                        <h4 className="font-semibold mb-2">Selected: {selectedTemplate.name}</h4>
                        <p className="text-sm text-slate-600 mb-3">
                            This template will pre-fill the following information:
                        </p>
                        <ul className="text-sm space-y-1 text-slate-700">
                            <li>- Conservative treatments (physiotherapy, analgesia)</li>
                            <li>- Clinical findings and symptom duration</li>
                            <li>- Body part: {selectedTemplate.body_part}</li>
                            <li>- Default urgency: {selectedTemplate.default_urgency}</li>
                        </ul>
                    </CardContent>
                </Card>
            )}

            <div className="flex justify-center gap-4">
                <Button 
                    variant="outline" 
                    onClick={onSkip}
                    data-testid="skip-template-btn"
                >
                    <FileText className="h-4 w-4 mr-2" />
                    Start from Scratch
                </Button>
                <Button 
                    onClick={handleConfirm}
                    disabled={!selectedTemplate}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="use-template-btn"
                >
                    Use Template
                    <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
            </div>

            {/* Cancer Referral Warning */}
            <div className="mt-6 p-4 bg-rose-50 border border-rose-200 rounded-lg">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <h4 className="font-semibold text-rose-800 text-sm">Important: Suspected Cancer Cases</h4>
                        <p className="text-sm text-rose-700 mt-1">
                            Patients with suspected malignancy should be referred through the established 
                            <strong> 2-Week Wait (2WW) Cancer Pathway</strong>. Do not use this system for 
                            suspected cancer referrals. Please follow your local cancer referral guidelines.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
